import { useState, useEffect } from "react";
import { useUser, useAuth } from "@clerk/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { User, X, Link2, MapPin, Search } from "lucide-react";
import { useUserProfile } from "@/hooks/useUserProfile";
import { authFetch, api } from "@/lib/api";
import "./profile.css";

export default function ProfilePage() {
  const { user, isLoaded } = useUser();
  const { isSignedIn, signOut, getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [revealed, setRevealed] = useState(false);

  const { data: profileData, isLoading: isProfileLoading } = useUserProfile();

  // Location editor state
  const [editingLocation, setEditingLocation] = useState(false);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");

  // De-dupe search state
  const [linkSearch, setLinkSearch] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: "/sign-in" });
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (profileData?.profile) {
      setCity(profileData.profile.city ?? "");
      setState(profileData.profile.state ?? "");
      setCountry(profileData.profile.country ?? "");
    }
  }, [profileData?.profile]);

  const updateProfileMutation = useMutation({
    mutationFn: async (body: { city?: string; state?: string; country?: string }) => {
      const token = await getToken();
      return authFetch("/users/profile", { method: "POST", body, token });
    },
    onSuccess: () => {
      setEditingLocation(false);
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
    },
  });

  const unclaimMutation = useMutation({
    mutationFn: async (athleteId: string) => {
      const token = await getToken();
      return authFetch(`/users/claim-athlete/${athleteId}`, {
        method: "DELETE",
        token,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
    },
  });

  const linkMutation = useMutation({
    mutationFn: async (athleteId: string) => {
      const token = await getToken();
      return authFetch("/users/link-athlete", {
        method: "POST",
        body: { athleteId },
        token,
      });
    },
    onSuccess: () => {
      setLinkSearch("");
      setLinkError(null);
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
    },
    onError: (err: Error) => {
      setLinkError(err.message);
    },
  });

  const debouncedSearch = linkSearch.trim();
  const { data: searchResults } = useQuery({
    queryKey: ["athlete-search-link", debouncedSearch],
    queryFn: async () => {
      if (!debouncedSearch) return [];
      const { data } = await api.GET("/search/athletes", {
        params: { query: { q: debouncedSearch } },
      });
      return (data as any[]) ?? [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  if (!isLoaded || !isSignedIn) return null;

  const profile = profileData?.profile;
  const claimedAthletes = profileData?.claimedAthletes ?? [];
  const claimedIds = new Set(claimedAthletes.map((a) => a.id));

  const locationDisplay = profile
    ? [profile.city, profile.state, profile.country].filter(Boolean).join(", ") || null
    : null;

  return (
    <div className="profile-page">
      <section className={cn("profile-section", revealed && "revealed")}>
        <div className="profile-card">
          <div className="profile-avatar">
            {user?.imageUrl ? (
              <img src={user.imageUrl} alt="" className="profile-avatar-img" />
            ) : (
              <User className="profile-avatar-icon" />
            )}
          </div>

          <h1 className="profile-name">
            {user?.firstName || user?.lastName
              ? `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()
              : "Athlete"}
          </h1>

          {user?.primaryEmailAddress && (
            <p className="profile-email">{user.primaryEmailAddress.emailAddress}</p>
          )}

          {locationDisplay && (
            <p className="profile-location">
              <MapPin size={13} />
              {locationDisplay}
            </p>
          )}

          <div className="profile-divider" />

          {/* Location Editor */}
          {profile && (
            <div className="profile-location-section">
              <div className="profile-section-header">
                <h2 className="profile-section-title">
                  <MapPin size={14} />
                  Location
                </h2>
                <button
                  type="button"
                  className="profile-edit-btn"
                  onClick={() => setEditingLocation(!editingLocation)}
                >
                  {editingLocation ? "Cancel" : "Edit"}
                </button>
              </div>

              {editingLocation && (
                <form
                  className="profile-location-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateProfileMutation.mutate({
                      city: city || undefined,
                      state: state || undefined,
                      country: country || undefined,
                    });
                  }}
                >
                  <input
                    className="profile-input"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                  <input
                    className="profile-input"
                    placeholder="State / Province"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                  <input
                    className="profile-input"
                    placeholder="Country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="profile-save-btn"
                    disabled={updateProfileMutation.isPending}
                  >
                    {updateProfileMutation.isPending ? "Saving..." : "Save location"}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Claimed Athletes */}
          <div className="profile-claimed-section">
            <h2 className="profile-section-title">Claimed Athletes</h2>
            {isProfileLoading && (
              <p className="profile-muted">Loading...</p>
            )}
            {!isProfileLoading && claimedAthletes.length === 0 && (
              <p className="profile-muted">
                No athletes claimed yet. Search for your name in{" "}
                <Link to="/records" search={{}} className="profile-link">
                  Records
                </Link>{" "}
                and click &ldquo;This is me&rdquo; to claim your results.
              </p>
            )}
            {claimedAthletes.length > 0 && (
              <ul className="profile-athletes-list">
                {claimedAthletes.map((a) => (
                  <li key={a.id} className="profile-athlete-row">
                    <Link
                      to="/athletes/$athleteId"
                      params={{ athleteId: a.id }}
                      className="profile-athlete-link"
                    >
                      <span className="profile-athlete-name">{a.fullName}</span>
                      <span className="profile-athlete-loc">
                        {[a.city, a.state, a.country].filter(Boolean).join(", ") || "—"}
                      </span>
                    </Link>
                    {a.mergedIntoId && (
                      <span className="profile-athlete-badge">
                        <Link2 size={11} /> linked
                      </span>
                    )}
                    <button
                      type="button"
                      className="profile-athlete-remove"
                      onClick={() => unclaimMutation.mutate(a.id)}
                      title="Remove claim"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* De-dupe: Link another athlete */}
          {claimedAthletes.length > 0 && (
            <div className="profile-link-section">
              <h2 className="profile-section-title">
                <Link2 size={14} />
                Link Duplicate
              </h2>
              <p className="profile-muted">
                If you raced under a different organization, search for your
                other athlete record to unify your results.
              </p>
              <div className="profile-search-wrap">
                <Search size={14} className="profile-search-icon" />
                <input
                  className="profile-input profile-search-input"
                  placeholder="Search by name..."
                  value={linkSearch}
                  onChange={(e) => {
                    setLinkSearch(e.target.value);
                    setLinkError(null);
                  }}
                />
              </div>
              {linkError && <p className="profile-error">{linkError}</p>}
              {searchResults && searchResults.length > 0 && (
                <ul className="profile-search-results">
                  {searchResults
                    .filter((a: any) => !claimedIds.has(a.id))
                    .slice(0, 8)
                    .map((a: any) => (
                      <li key={a.id} className="profile-search-row">
                        <div className="profile-search-info">
                          <span className="profile-search-name">{a.fullName}</span>
                          <span className="profile-search-loc">
                            {[a.city, a.state, a.country].filter(Boolean).join(", ") ||
                              "—"}{" "}
                            · {a.resultCount ?? 0} results
                          </span>
                        </div>
                        <button
                          type="button"
                          className="profile-link-btn"
                          onClick={() => linkMutation.mutate(a.id)}
                          disabled={linkMutation.isPending}
                        >
                          <Link2 size={13} />
                          Link
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}

          <div className="profile-divider" />

          <button
            type="button"
            className="profile-signout"
            onClick={() => signOut()}
          >
            Sign out
          </button>
        </div>
      </section>
    </div>
  );
}
